import type { QueryClient } from '@tanstack/react-query';
import {
  getApiProfileGiftcardsQueryKey,
  getApiProfileOrdersQueryKey,
  getApiReceiptByEncryptedOrderIdQueryKey,
} from 'api/generated/@tanstack/react-query.gen';
import type {
  ExchangeNewTicketPriceDto,
  ExchangeReturnedTicketDto,
  FinalizeExchangeRequest,
  OrderResponse,
  TicketDto,
} from 'api/generated/types.gen';
import type { TempExchangeData } from 'lib/app-state';

/**
 * The exchangeable ticket group an order holds for an event, if any.
 *
 * An order can hold both subscription and plain tickets for the same event
 * under different change rules, so the group also carries which subscription
 * type it belongs to (undefined for plain tickets). That value selects both the
 * rule set and which of the old tickets are handed back, so it has to travel
 * with the exchange from the options call all the way to `/start`.
 */
export function getExchangeableTicketGroup(
  order: OrderResponse | undefined,
  eventId: number
): { subscriptionTypeId?: number } | null {
  const isExchangeable = (group: { eventId?: number } & Record<string, any>) =>
    group.eventId === eventId &&
    group.canExchangeTickets &&
    (group.tickets?.length ?? 0) > 0;

  for (const subscription of order?.subscriptionTypeGroups ?? []) {
    if ((subscription.eventTicketGroups ?? []).some(isExchangeable)) {
      return { subscriptionTypeId: subscription.subscriptionTypeId };
    }
  }
  if ((order?.tickets ?? []).some(isExchangeable)) return {};
  return null;
}

/** Path to the "Choose a new event" step for an order's tickets to an event.
 *  Carries the original order id and, for subscription tickets, the
 *  subscription type whose rules apply. */
export function getChangeTicketsHref(
  encryptedOrderId: string,
  eventId: number,
  subscriptionTypeId?: number
): string {
  const params = new URLSearchParams({ orderId: encryptedOrderId });
  // Omitted entirely for plain tickets — a different value than the one used at
  // options yields a different rule set and a different set of old tickets.
  if (subscriptionTypeId != null) {
    params.set('subscriptionTypeId', String(subscriptionTypeId));
  }
  return `/account/event/${eventId}/change-tickets?${params.toString()}`;
}

/**
 * Whether an event is the one a change is moving tickets into. True for the
 * whole change: the rules restricting which ticket types and price zones the
 * new tickets may hold apply to every edit of them, not only to the first pick.
 */
export function isExchangeTargetEvent(
  exchange: TempExchangeData | null | undefined,
  eventId: number | undefined
): boolean {
  return (
    !!exchange && eventId != null && exchange.selectedEvent?.eventId === eventId
  );
}

/** Whether the new tickets are still being chosen — the change is on this event
 *  and has not started, so no staging order holds its seats yet. */
export function isExchangeSeatSelection(
  exchange: TempExchangeData | null | undefined,
  eventId: number | undefined
): boolean {
  return isExchangeTargetEvent(exchange, eventId) && !exchange?.exchangeOrderId;
}

/** Whether the seats of a change that has already started are being edited.
 *  Its staging order owns them, so the cart re-enters the picker as an ordinary
 *  order edit. */
export function isExchangeOrderEdit(
  exchange: TempExchangeData | null | undefined,
  eventId: number | undefined
): boolean {
  return (
    isExchangeTargetEvent(exchange, eventId) && !!exchange?.exchangeOrderId
  );
}

/**
 * The ticket types a cart ticket may be switched to, narrowed to what a change
 * in progress allows on its new event. An empty list leaves the ticket showing
 * its type as plain text — which is what a rule fixing the type to a single one
 * means for the buyer.
 */
export function getExchangeTicketTypeOptions<T extends { value: number }>(
  options: T[],
  exchange: TempExchangeData | null | undefined,
  eventId: number
): T[] {
  if (!isExchangeTargetEvent(exchange, eventId)) return options;
  const allowedIds = getAllowedExchangeTicketTypeIds(
    options.map((option) => option.value),
    exchange?.allowedTicketTypeIds
  );
  const isRestricted =
    allowedIds.length > 0 && !!exchange?.allowedTicketTypeIds?.length;
  if (!isRestricted) return options;
  if (allowedIds.length <= 1) return [];
  return options.filter((option) => allowedIds.includes(option.value));
}

/**
 * How checkout settles the order in front of it.
 *
 * - `order` — not an exchange; the ordinary purchase or payment path.
 * - `pricing` — an exchange whose cart has not been read yet. Its amounts are
 *   all zero until then, so nothing may branch on them and the terminal button
 *   stays disabled.
 * - `payment` — the new tickets cost more than the returned ones, so the
 *   difference is paid through the payment provider.
 * - `noPayment` — an equal, free or cheaper swap. No payment session is opened,
 *   because a session is created the moment the provider is enabled and that
 *   would settle the swap before the buyer confirmed anything.
 */
export type ExchangeSettlement = 'order' | 'pricing' | 'payment' | 'noPayment';

export function getExchangeSettlement({
  isExchange,
  isCartPending,
  amountDue,
}: {
  isExchange: boolean;
  isCartPending: boolean;
  amountDue: number;
}): ExchangeSettlement {
  if (!isExchange) return 'order';
  if (isCartPending) return 'pricing';
  return amountDue > 0 ? 'payment' : 'noPayment';
}

/** Encrypted ticket ids of the seats reserved for the new event, used to start
 *  an exchange. Skips any ticket missing an encrypted id. */
export function getEncryptedNewTicketIds(
  tickets: TicketDto[] | undefined
): string[] {
  return (tickets ?? [])
    .map((t) => t.encryptedId)
    .filter((id): id is string => id != null);
}

/** Counts the tickets being exchanged by one of their ids, capped at the number
 *  of tickets the exchange covers. */
function countExchangedTickets(
  tickets: TicketDto[] | undefined,
  ticketCount: number,
  pick: (ticket: TicketDto) => number | null | undefined
): Record<number, number> {
  const counts: Record<number, number> = {};
  let counted = 0;
  for (const ticket of tickets ?? []) {
    if (counted >= ticketCount) break;
    const id = pick(ticket);
    if (id == null) continue;
    counts[id] = (counts[id] ?? 0) + 1;
    counted += 1;
  }
  return counts;
}

/** Per-type counts of the tickets being exchanged, keyed by ticket type id and
 *  capped at the number of tickets the exchange covers. */
export function getExchangeTicketTypeCounts(
  tickets: TicketDto[] | undefined,
  ticketCount: number
): Record<number, number> {
  return countExchangedTickets(tickets, ticketCount, (t) => t.ticketTypeId);
}

/** Per-zone counts of the tickets being exchanged, keyed by price zone id and
 *  capped at the number of tickets the exchange covers. */
export function getExchangePriceZoneCounts(
  tickets: TicketDto[] | undefined,
  ticketCount: number
): Record<number, number> {
  return countExchangedTickets(tickets, ticketCount, (t) => t.priceZoneId);
}

/** Ticket type ids the buyer may pick on the new event: the event's own types,
 *  narrowed to the exchange rules' allowed list when it has one. */
export function getAllowedExchangeTicketTypeIds(
  eventTicketTypeIds: number[],
  allowedTicketTypeIds: number[] | undefined | null
): number[] {
  if (!allowedTicketTypeIds?.length) return eventTicketTypeIds;
  return eventTicketTypeIds.filter((id) => allowedTicketTypeIds.includes(id));
}

/**
 * Seed quantities for the new event, mirroring the ticket types of the tickets
 * being exchanged. Counts for types the new event doesn't offer (or that the
 * rules disallow) fall back to the first allowed type, as does the whole count
 * when the old types are unknown.
 *
 * Returns null when no ticket type is available to seed.
 */
export function getExchangePrefillQuantities({
  ticketCount,
  ticketTypeCounts,
  allowedTicketTypeIds,
}: {
  ticketCount: number;
  ticketTypeCounts: Record<number, number> | undefined;
  allowedTicketTypeIds: number[];
}): Record<number, number> | null {
  const fallbackTypeId = allowedTicketTypeIds[0];
  if (!ticketCount || fallbackTypeId == null) return null;

  const quantities: Record<number, number> = {};
  let assigned = 0;
  for (const [id, count] of Object.entries(ticketTypeCounts ?? {})) {
    const ticketTypeId = Number(id);
    if (!count || !allowedTicketTypeIds.includes(ticketTypeId)) continue;
    const quantity = Math.min(count, ticketCount - assigned);
    if (quantity <= 0) break;
    quantities[ticketTypeId] = (quantities[ticketTypeId] ?? 0) + quantity;
    assigned += quantity;
  }

  const remainder = ticketCount - assigned;
  if (remainder > 0) {
    quantities[fallbackTypeId] = (quantities[fallbackTypeId] ?? 0) + remainder;
  }
  return quantities;
}

/**
 * Price zone ids the buyer may pick on the new event, or null when the rules
 * leave the choice open.
 *
 * The rules' allowed list is the authority: the backend fills it with the zones
 * of exactly the tickets being handed back, and only when `samePriceZone` is on,
 * so it already encodes the rule. `priceZoneCounts` is the same set re-derived
 * locally and is only a fallback for when that list is absent or empty, which
 * count alike — it is scoped by whatever the caller counted, so preferring it
 * can admit a zone the swap is rejected for.
 *
 * A restriction that matches no zone on this event is ignored, so the buyer is
 * never left without one (the backend rejects a disallowed zone anyway).
 */
export function getAllowedExchangePriceZoneIds(
  eventPriceZoneIds: number[],
  {
    samePriceZone,
    priceZoneCounts,
    allowedPriceZoneIds,
  }: {
    samePriceZone?: boolean;
    priceZoneCounts?: Record<number, number>;
    allowedPriceZoneIds?: number[] | null;
  }
): number[] | null {
  const restrictedTo = allowedPriceZoneIds?.length
    ? allowedPriceZoneIds
    : samePriceZone
      ? Object.keys(priceZoneCounts ?? {}).map(Number)
      : [];
  if (restrictedTo.length === 0) return null;

  const allowed = eventPriceZoneIds.filter((id) => restrictedTo.includes(id));
  return allowed.length > 0 ? allowed : null;
}

/**
 * The price zone to auto-select seats in on entry: the zone most of the
 * exchanged tickets sit in, as long as the new event still offers it and the
 * rules allow it. Null when that zone is gone, leaving the choice to the
 * caller (the best available seats, or the first allowed zone).
 */
export function getExchangePreferredPriceZoneId(
  priceZoneCounts: Record<number, number> | undefined,
  selectableZoneIds: number[]
): number | null {
  let preferredId: number | null = null;
  let preferredCount = 0;
  for (const [id, count] of Object.entries(priceZoneCounts ?? {})) {
    const priceZoneId = Number(id);
    if (!count || count <= preferredCount) continue;
    if (!selectableZoneIds.includes(priceZoneId)) continue;
    preferredId = priceZoneId;
    preferredCount = count;
  }
  return preferredId;
}

/** Total value of the tickets being returned with their sales tax, which is the
 *  basis the summary's returned-tickets line is on (always ≥ 0). */
export function getReturnedTicketsTotal(
  returnedTickets: ExchangeReturnedTicketDto[] | undefined
): number {
  return (returnedTickets ?? []).reduce(
    (sum, t) => sum + (t.priceWithTax ?? 0),
    0
  );
}

/** What the staged tickets are billed at with their sales tax. Not the prices the
 *  staging order holds, which are the cheaper selection when it was price
 *  matched rather than what is charged for it. */
export function getNewTicketsTotal(
  newTicketPrices: ExchangeNewTicketPriceDto[] | undefined
): number {
  return (newTicketPrices ?? []).reduce(
    (sum, t) => sum + (t.priceWithTax ?? 0),
    0
  );
}

/** Where a cheaper swap is handed over to be settled: the list of refund
 *  targets when the rules name more than one of the chosen kind. */
export const refundTargetsHref = '/exchange/refund';

/**
 * Where the refund of a cheaper swap is settled — the landing page of the gift
 * card group or the donation the difference is turned into, or the list of them
 * when the rules offer several of the chosen kind. Null until a target kind has
 * been chosen, which is what keeps the checkout's Continue disabled.
 */
export function getRefundLandingHref(
  exchange: Pick<
    TempExchangeData,
    'refundGiftCardGroupId' | 'refundDonationId'
  > | null,
  targets: { giftCardGroupIds: number[]; donationIds: number[] }
): string | null {
  if (exchange?.refundGiftCardGroupId != null) {
    return targets.giftCardGroupIds.length > 1
      ? refundTargetsHref
      : `/giftcard/${exchange.refundGiftCardGroupId}`;
  }
  if (exchange?.refundDonationId != null) {
    return targets.donationIds.length > 1
      ? refundTargetsHref
      : `/donation/${exchange.refundDonationId}`;
  }
  return null;
}

/** Refund target for finalize, derived from the choice stored on the exchange
 *  temp data. Only one of the two ids is ever set. */
export function getFinalizeRefundBody(
  exchange: Pick<
    TempExchangeData,
    'refundGiftCardGroupId' | 'refundDonationId'
  > | null
): FinalizeExchangeRequest {
  return {
    refundGiftCardGroupId: exchange?.refundGiftCardGroupId ?? undefined,
    refundDonationId: exchange?.refundDonationId ?? undefined,
  };
}

/**
 * Drops what the app holds on the order an exchange was just applied to. The
 * swap rewrites an order bought earlier, so the receipt does not count it as a
 * fresh purchase and refresh these itself, while checkout has only just loaded
 * them, so they are still fresh enough to be shown as they were. The profile
 * gift cards cover the one a refund was taken as.
 */
export function invalidateExchangedOrder(
  queryClient: QueryClient,
  encryptedOrderId: string
) {
  queryClient.invalidateQueries({
    queryKey: getApiReceiptByEncryptedOrderIdQueryKey({
      path: { encryptedOrderId },
    }),
  });
  queryClient.invalidateQueries({ queryKey: getApiProfileOrdersQueryKey() });
  queryClient.invalidateQueries({ queryKey: getApiProfileGiftcardsQueryKey() });
}

/**
 * Returned tickets split into one group per event, in first-appearance order.
 *
 * An order can go through several exchanges, each handing back tickets to a
 * different event, so the tickets of one exchange are not the whole list.
 * Tickets without an event id collapse into a single group of their own — the
 * cart's returned tickets carry no id until the exchange has been priced.
 */
export function groupReturnedTicketsByEvent(
  returnedTickets: ExchangeReturnedTicketDto[] | undefined
): { eventId?: number; tickets: ExchangeReturnedTicketDto[] }[] {
  const groups = new Map<number | undefined, ExchangeReturnedTicketDto[]>();
  for (const ticket of returnedTickets ?? []) {
    const group = groups.get(ticket.eventId);
    if (group) group.push(ticket);
    else groups.set(ticket.eventId, [ticket]);
  }
  return [...groups].map(([eventId, tickets]) => ({ eventId, tickets }));
}
